import ClassNames from "classnames";
import { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";

import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";
import useDocumentTitle from "../../utils/useDocumentTitle";

import FlashCoachLogo from "../../components/_Icons/FlashCoachLogo";
import TDCXLogo from "../../components/_Icons/TDCXLogo";
import Button from "../../components/Button";
import Loader from "../../components/Loader";
import PasswordField from "../../components/PasswordField";
import LinkExpired from "./LinkExpired";

import styles from "./Login.module.scss";

export function ResetPassword(props) {
  useDocumentTitle(props.title);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenLoading, setIsTokenLoading] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const { addNotification } = useNotification();
  const { id } = useParams();
  const [isStrongPassword, setIsStrongPassword] = useState(false);
  const [isMatchingPassword, setIsMatchingPassword] = useState(false);
  const [loginForm, setLoginForm] = useState({
    password: "",
    confirmPassword: "",
    isDirty: false,
    showPassword: false,
  });

  useEffect(
    () => {
      setIsTokenLoading(true);
      apiRequest.get(`/api/verifyPasswordToken/${id}`).then(response => {
        let data = response.data;
        // if token is valid, do nothing
        if (data.status && data.status === "success") {
          setIsValidToken(true);
        } else {
          setIsValidToken(false);
        }
        setIsTokenLoading(false);
      });
    },
    // eslint-disable-next-line
    []
  );

  useEffect(() => {
    if (loginForm.isDirty) {
      validateStrength();
      validateMatch();
    }
    // eslint-disable-next-line
  }, [loginForm.password, loginForm.confirmPassword, loginForm.isDirty]);

  const handleLoginFormEventChange = prop => event => {
    setLoginForm({
      ...loginForm,
      isDirty: true,
      [prop]: event.target.value,
    });
  };

  const handleClickShowPassword = () => {
    setLoginForm({
      ...loginForm,
      showPassword: !loginForm.showPassword,
    });
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const handleResetPassword = async () => {
    let valid = validateMatch() && validateStrength();
    if (!valid) {
      addNotification(
        "Can't change password! Please refresh the page or check if your new password meets the requirements."
      );
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiRequest.post(`/api/resetPassword/${id}`, {
        token: id,
        password: loginForm.password,
        confirm_password: loginForm.confirmPassword,
      });
      let data = response.data;
      // if login success (status != "fail")
      if (!(data.status && data.status === "fail")) {
        addNotification("Your password reset successfully!", "success");
        history.push("/");
        setIsLoading(false);
      } else if (data.status === "fail") {
        addNotification(data.msg, "error");
        setIsLoading(false);
      }
    } catch (error) {
      addNotification("something went wrong, please try again", "error");
      setIsLoading(false);
    }
  };

  const validateMatch = () => {
    setIsMatchingPassword(loginForm.password === loginForm.confirmPassword);
    return loginForm.password === loginForm.confirmPassword;
  };

  const validateStrength = () => {
    const lengthCheck = loginForm.password.length >= 12;
    const numberCheck = /\d/.test(loginForm.password);
    const specialCheck = /[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(
      loginForm.password
    );
    const upperCaseCheck = /[A-Z]/.test(loginForm.password);

    // Password must be at least 12 characters long, one number, one special character and one upper case letter.
    setIsStrongPassword(
      lengthCheck && numberCheck && specialCheck && upperCaseCheck
    );
    return lengthCheck && numberCheck && specialCheck && upperCaseCheck;
  };

  // Handle invalid token logic
  if (isTokenLoading) {
    return <Loader />;
  }
  if (!isValidToken) {
    return <LinkExpired />;
  }

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      handleResetPassword()
    }}>
      <div className={styles.pageWrapper}>
        <div className={styles.loginBlock}>
          <div className={styles.logo}>
            <FlashCoachLogo />
          </div>
          <div className={styles.loginContent}>
            <div className={styles.loginContent__formControl}>
              <PasswordField
                autoComplete="current-password"
                label="Password"
                show={loginForm.showPassword}
                value={loginForm.password}
                onChange={handleLoginFormEventChange("password")}
                onShowClick={handleClickShowPassword}
                onShowMouseDown={handleMouseDownPassword}
              />
              {loginForm.password.length > 0 &&
                loginForm.isDirty &&
                !isStrongPassword && (
                  <span className={"margin-bottom-24 red-700"}>
                    Password must be at least 12 characters long, one number, one
                    special character and one upper case letter.
                  </span>
                )}
            </div>
            <div className={styles.loginContent__formControl}>
              <PasswordField
                autoComplete="current-password"
                label="Confirm Password"
                show={loginForm.showPassword}
                value={loginForm.confirmPassword}
                onChange={handleLoginFormEventChange("confirmPassword")}
                onShowClick={handleClickShowPassword}
                onShowMouseDown={handleMouseDownPassword}
              />
              {loginForm.confirmPassword.length > 0 &&
                loginForm.isDirty &&
                !isMatchingPassword && (
                  <span className={"margin-top-24 red-700"}>
                    Passwords do not match
                  </span>
                )}
            </div>
            <div
              className={ClassNames(
                styles.loginContent__formControl,
                styles.loginContent__actions
              )}
            >
              {/* TODO: link to forgot password page */}
              <div className={styles.right}>
                <Link to="/" className={styles.loginForm__forgotPassword}>
                  Back to Login
                </Link>
              </div>
            </div>
            <div
              className={ClassNames(
                styles.loginContent__formControl,
                styles.loginContent__submitBtnWrapper
              )}
            >
              <Button
                type="submit"
                color="primary"
                style={{ backgroundColor: "#f5b019" }}
                variant="contained"
                fullWidth
                isloading={isLoading ? 1 : 0}
              >
                {"Reset Password"}
              </Button>
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.logo}>
            <TDCXLogo width={100} />
          </div>
          <div className={styles.copyright}>
            Copyright © 2021 TDCX. All Rights Reserved.
          </div>
        </div>
      </div>
    </form>
  );
}
